import React, {useState, useEffect} from 'react';
import {Transforms} from 'slate';
import {ReactEditor} from 'slate-react';

import AnchorOptionsMenu from '../AnchorOptionsMenu';
import {useEditorUI} from '../editor-core/contexts/EditorUIContext';

import {ActionButtons} from './ActionButtons';
import {getWrapStyle} from './getWrapStyle';
import {ResizableElement, ResizeContext} from './ResizableElement';

export const ImageRenderer = ({
  attributes,
  children,
  element,
  selected,
  focused,
  editor
}: any): React.JSX.Element => {
  const [localState, setLocalState] = useState<{
    width?: number;
    height?: number;
    crop?: any;
  } | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [isDraggingCrop] = useState(false);
  const {setCroppingImage} = useEditorUI();
  const wrapStyle = getWrapStyle(element.float || 'none');

  const crop = localState?.crop ??
    element.crop ?? {top: 0, left: 0, bottom: 0, right: 0};

  const safeCrop = {
    top: Math.max(0, Math.min(crop.top, 99)),
    bottom: Math.max(
      0,
      Math.min(crop.bottom, 99 - Math.max(0, Math.min(crop.top, 99)))
    ),
    left: Math.max(0, Math.min(crop.left, 99)),
    right: Math.max(
      0,
      Math.min(crop.right, 99 - Math.max(0, Math.min(crop.left, 99)))
    )
  };

  const zoomX = 100 / (100 - safeCrop.left - safeCrop.right);
  const zoomY = 100 / (100 - safeCrop.top - safeCrop.bottom);
  const imgWidth = zoomX * 100;
  const imgHeight = zoomY * 100;
  const imgLeft = -safeCrop.left * zoomX;
  const imgTop = -safeCrop.top * zoomY;

  useEffect(() => {
    if (!isDraggingCrop && localState) {
      const path = ReactEditor.findPath(editor, element);
      Transforms.setNodes(
        editor,
        {
          width: localState.width,
          height: localState.height,
          crop: localState.crop
        } as any,
        {at: path}
      );
      setLocalState(null);
    }
  }, [isDraggingCrop, editor, element]);

  return (
    <div
      {...attributes}
      contentEditable={false}
      className="editor-image-container"
      style={{
        position: 'relative',
        float: wrapStyle.float,
        margin: wrapStyle.margin,
        display: wrapStyle.display,
        clear: wrapStyle.clear,
        maxWidth: wrapStyle.maxWidth,
        verticalAlign: wrapStyle.verticalAlign,
        shapeOutside: wrapStyle.shapeOutside,
        WebkitShapeOutside: wrapStyle.WebkitShapeOutside,
        shapeMargin: wrapStyle.shapeMargin
      }}
    >
      <div
        style={{
          display: 'block',
          width: 'fit-content',
          maxWidth: '100%',
          marginLeft:
            element.align === 'center' || element.align === 'right'
              ? 'auto'
              : '0',
          marginRight: element.align === 'center' ? 'auto' : '0'
        }}
      >
        <ResizableElement element={element} disabled={isCropping}>
          <ResizeContext.Consumer>
            {(resize) => (
              <div
                className="render-element-wrapper"
                style={{
                  display: 'inline-block',
                  boxShadow:
                    selected && focused && !isCropping
                      ? '0 0 0 3px #B4D5FF'
                      : 'none',
                  width:
                    localState?.width ?? resize?.width ?? element.width ?? 200,
                  height:
                    localState?.height ??
                    resize?.height ??
                    element.height ??
                    150,
                  position: 'relative'
                }}
              >
                {!isCropping && (
                  <ActionButtons
                    editor={editor}
                    element={element}
                    onToggleCrop={() => {
                      setIsCropping(true);
                    }}
                    setCroppingImage={setCroppingImage}
                  />
                )}
                {!isCropping && <AnchorOptionsMenu element={element} />}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    pointerEvents: 'none'
                  }}
                >
                  <img
                    src={element.url}
                    alt="inserted"
                    style={{
                      position: 'absolute',
                      width: `${imgWidth}%`,
                      height: `${imgHeight}%`,
                      left: `${imgLeft}%`,
                      top: `${imgTop}%`,
                      objectFit: 'fill'
                    }}
                  />
                </div>
              </div>
            )}
          </ResizeContext.Consumer>
        </ResizableElement>
      </div>
      {children}
    </div>
  );
};
